<script setup lang="ts">
import { ref, watch, inject, onUnmounted, type Ref } from "vue";
import { formatPercent } from "@/composables/useChartUtils";

const props = defineProps<{ value: number; delay?: number }>();

// Counts up from 0 in sync with the bar growth when the card is revealed on scroll
const revealed = inject<Ref<boolean>>("resultsCardRevealed", ref(true));

const displayed = ref(revealed.value ? props.value : 0);

let raf = 0;
let timer: ReturnType<typeof setTimeout> | undefined;

function cancel() {
  cancelAnimationFrame(raf);
  clearTimeout(timer);
}

function tweenTo(target: number, delay = 0) {
  cancel();
  timer = setTimeout(() => {
    const from = displayed.value;
    const start = performance.now();
    const duration = 350;
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      displayed.value = from + (target - from) * eased;
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  }, delay);
}

watch(revealed, (isRevealed) => {
  if (isRevealed) {
    tweenTo(props.value, props.delay || 0);
  } else {
    cancel();
    displayed.value = 0;
  }
});

watch(
  () => props.value,
  (value) => {
    if (revealed.value) tweenTo(value);
  },
);

onUnmounted(cancel);
</script>

<template>{{ formatPercent(displayed) }}</template>
